import { api } from "@/lib/api";

const CHUNK_SIZE = 512 * 1024; // 512 KB per chunk to be safe under proxies

export const ALLOWED_MIMES = [
    "image/jpeg", "image/png", "image/webp", "image/gif",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/msword",
    "application/vnd.ms-excel",
    "application/vnd.ms-powerpoint",
    "text/plain",
];

export const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export async function uploadFileChunked(file, { ticketId, onProgress } = {}) {
    if (file.size > MAX_SIZE_BYTES) {
        throw new Error("El archivo excede el límite de 10 MB");
    }
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

    const { data: initData } = await api.post("/attachments/init", {
        filename: file.name,
        mime: file.type || "application/octet-stream",
        total_size: file.size,
        total_chunks: totalChunks,
    });

    for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", blob, `chunk-${i}`);
        await api.post(`/attachments/${initData.attachment_id}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        onProgress?.(((i + 1) / totalChunks) * 100);
    }

    const params = ticketId ? `?ticket_id=${encodeURIComponent(ticketId)}` : "";
    const { data } = await api.post(`/attachments/${initData.attachment_id}/complete${params}`);
    return data;
}

export function downloadUrl(attachmentId, { inline = false } = {}) {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = (typeof window !== "undefined") ? (localStorage.getItem("itotal4u_token") || "") : "";
    const params = new URLSearchParams();
    if (inline) params.set("disposition", "inline");
    if (token) params.set("token", token);
    const qs = params.toString();
    return `${base}/api/attachments/${attachmentId}/download${qs ? "?" + qs : ""}`;
}

export const OFFICE_MIMES = [
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",  // docx
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",        // xlsx
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",  // pptx
    "application/msword",                                                        // doc
    "application/vnd.ms-excel",                                                  // xls
    "application/vnd.ms-powerpoint",                                             // ppt
];

export function isOfficeDoc(mime) {
    return OFFICE_MIMES.includes(mime);
}

export function isViewableInline(mime) {
    if (!mime) return false;
    if (mime.startsWith("image/")) return true;
    if (mime === "application/pdf" || mime === "text/plain") return true;
    if (isOfficeDoc(mime)) return true;
    return false;
}

export function officeOnlineViewerUrl(signedUrl) {
    // Microsoft Office Online Viewer — renderiza docx/xlsx/pptx (también abre Google Docs exportados)
    return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(signedUrl)}`;
}

export async function openInlineViewer(attachmentId, mime) {
    // For Office docs, fetch signed URL then open Office Online Viewer
    // For other types, use direct token URL
    const { api } = await import("@/lib/api");
    if (isOfficeDoc(mime)) {
        const { data } = await api.get(`/attachments/${attachmentId}/signed-url`, { params: { ttl_seconds: 600 } });
        const viewerUrl = officeOnlineViewerUrl(data.url);
        window.open(viewerUrl, "_blank", "noopener,noreferrer");
        return;
    }
    // Non-Office: direct inline view with token
    window.open(downloadUrl(attachmentId, { inline: true }), "_blank", "noopener,noreferrer");
}

export function humanFileSize(bytes) {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
